import {
  type Observable,
  firstValueFrom,
  map,
  mergeWith,
  race,
  Subject,
  NEVER,
} from 'rxjs'
import { fromAbortSignal } from '@/utils/rx-util'

export interface AsyncPromptOptions {
  /**
   * When this emits, any ongoing prompt will be aborted
   * and return `null`.
   *
   * @remarks A common example is a modal close signal.
   */
  abort$?: Observable<unknown>
}

/**
 * Utility for showing a prompt and waiting for a user response with
 * cancellation support.
 */
export class AsyncPrompt<T> {
  private readonly _response$ = new Subject<T>()
  private readonly _sharedAbort$: Observable<unknown>

  /**
   * Constructor.
   */
  constructor({ abort$ }: AsyncPromptOptions) {
    this._sharedAbort$ = abort$ ?? NEVER
  }

  /**
   * Starts a prompt and waits for a response or abort.
   *
   * @param signal Abort the prompt when the signal cancels.
   */
  async prompt(signal?: AbortSignal): Promise<T | null> {
    return await firstValueFrom(
      race([
        this._response$.pipe(
          mergeWith(this._sharedAbort$.pipe(map(() => null))),
        ),
        fromAbortSignal(signal).pipe(map(() => null)),
      ]),
      { defaultValue: null },
    )
  }

  /**
   * Responds to the prompt with a value, resolving any active prompts.
   *
   * @param value
   */
  respond(value: T) {
    return this._response$.next(value)
  }
}
